import { History } from 'history'
import { callFunction } from '../../tools/dispatcher'
import { ToolCallOutput } from '../types'

interface HandleToolCallParams {
  output: any
  history: History<unknown>
  osAssistantCode: string | null
  orgId: number | null
  isMountedRef: React.MutableRefObject<boolean>
  setToolCall: (toolCall: any | null) => void
  setSessionToolCalls: React.Dispatch<React.SetStateAction<any[]>>
  getDataChannelInCallback: () => RTCDataChannel | null
  sendClientEvent: (event: any) => void
  currentSessionStateAppliedRef: React.MutableRefObject<{
    tools: any[]
    [key: string]: any
  }>
}

/**
 * Handle a tool call from the voice API
 */
export const handleToolCall = async ({
  output,
  history,
  osAssistantCode,
  orgId,
  isMountedRef,
  setToolCall,
  setSessionToolCalls,
  getDataChannelInCallback,
  sendClientEvent,
  currentSessionStateAppliedRef,
}: HandleToolCallParams): Promise<void> => {
  const toolCall = {
    name: output.name,
    arguments:
      typeof output.arguments === 'string' ? JSON.parse(output.arguments) : output.arguments || {},
  }

  const toolDefinition = currentSessionStateAppliedRef.current.tools.find((tool) => tool.name === toolCall.name)
  if (!toolDefinition) {
    throw new Error(`Unknown tool call: ${toolCall.name}`)
  }

  // Set tool call state immediately so visual indicator shows
  if (isMountedRef.current) {
    setToolCall(toolCall)
  }

  // TOOL CALL HANDLING
  // Initialize toolCallOutput with a default response
  const toolCallOutput: ToolCallOutput = {
    response: `Tool call ${toolCall.name} executed successfully.`,
  }

  try {
    if (!osAssistantCode) {
      throw new Error('Voice session assistant code is not available')
    }

    toolCallOutput.result = await callFunction(
      { osAssistantCode, name: toolCall.name, args: toolCall.arguments, id: toolCall.name },
      history,
      orgId ?? undefined
    )

    // Use the ref to get the current data channel value
    const _dataChannel = getDataChannelInCallback()
    if (!_dataChannel) {
      console.error('[Voice] Cannot send tool call response - data channel not initialized')
      // Clear tool call state even if data channel is not available
      if (isMountedRef.current) {
        setToolCall(null)
      }
      return
    }

    if (_dataChannel.readyState !== 'open') {
      console.error(
        '[Voice] Cannot send tool call response - data channel not open (state:',
        _dataChannel.readyState,
        ')'
      )
      // Clear tool call state even if data channel is not open
      if (isMountedRef.current) {
        setToolCall(null)
      }
      return
    }

    sendClientEvent({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: output.call_id,
        output: JSON.stringify(toolCallOutput),
      },
    })

    // Record tool call for transcript (only if successful)
    if (isMountedRef.current) {
      setSessionToolCalls((prev) => [
        ...prev,
        {
          toolName: toolCall.name,
          arguments: toolCall.arguments,
          timestamp: Date.now(),
        },
      ])
    }

    // Clear tool call state after execution completes
    if (isMountedRef.current) {
      setToolCall(null)
    }

    // Force a model response to make sure it responds after certain tool calls
    if (!toolDefinition.silent) {
      sendClientEvent({
        type: 'response.create',
      })
    }
  } catch (error: any) {
    console.error('[Voice] Tool call error:', toolCall.name, error)

    // Send error response back to API so Ada can respond
    const _dataChannel = getDataChannelInCallback()
    if (_dataChannel && _dataChannel.readyState === 'open') {
      try {
        sendClientEvent({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: output.call_id,
            output: JSON.stringify({
              response: `Tool call ${toolCall.name} failed: ${error.message || 'Unknown error'}`,
              error: error.message || 'Unknown error',
            }),
          },
        })
        // Force a model response so Ada can handle the error
        sendClientEvent({
          type: 'response.create',
        })
      } catch (sendError) {
        console.error('[Voice] Failed to send error response:', sendError)
      }
    }

    // Record failed tool call for transcript
    if (isMountedRef.current) {
      setSessionToolCalls((prev) => [
        ...prev,
        {
          toolName: toolCall.name,
          arguments: toolCall.arguments,
          timestamp: Date.now(),
          failed: true,
          error: error.message || 'Unknown error',
        },
      ])
    }

    // Clear tool call state even on error
    if (isMountedRef.current) {
      setToolCall(null)
    }

    // Re-throw the error so it can be handled upstream (but we've already sent the response)
    throw error
  }
}
